import React from "react";

/**
 * FieldInput — Warm Precision design-system primitive for text inputs,
 * selects, and textareas.
 *
 * Use for: any form control that takes typed or chosen input. The wrapper
 * enforces consistent border, radius, focus ring, disabled state, and an
 * `invalid` red-border state — so pages don't trip the design-system
 * validator with one-off `<input className="border rounded …">`.
 *
 * Pattern:
 *   <FormRow label="Name">
 *     <FieldInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme" />
 *   </FormRow>
 *
 *   <FieldInput as="select" value={tier} onChange={(e) => setTier(e.target.value)}>
 *     <option value="solo">Solo</option>
 *     <option value="pro">Pro</option>
 *   </FieldInput>
 *
 *   <FieldInput as="textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
 *
 *   <FieldInput invalid value={email} onChange={...} aria-describedby="email-error" />
 *
 * For pixel-valued numbers, prefer <NumberInputPx>. For text/email/url/etc.,
 * use this. For radio/checkbox, keep using the native control with a label.
 */

type BaseInvalid = { invalid?: boolean };

type InputProps = BaseInvalid &
  React.InputHTMLAttributes<HTMLInputElement> & { as?: "input" };
type SelectProps = BaseInvalid &
  React.SelectHTMLAttributes<HTMLSelectElement> & { as: "select" };
type TextareaProps = BaseInvalid &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { as: "textarea" };

type Props = InputProps | SelectProps | TextareaProps;

const BASE_CLS =
  "w-full text-sm text-zinc-800 bg-white rounded-md px-3 py-2 transition-shadow placeholder:text-zinc-400 disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed focus:outline-none";

const BORDER_OK =
  "border border-zinc-200 focus:border-[color:var(--color-primary)] focus:ring-2 focus:ring-[color:var(--color-primary)]/20";

const BORDER_INVALID =
  "border border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-500/20";

const FieldInput = React.forwardRef<
  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  Props
>(function FieldInput(props, ref) {
  const { as = "input", invalid, className = "", ...rest } = props as Props & {
    as?: "input" | "select" | "textarea";
    className?: string;
  };
  const borderCls = invalid ? BORDER_INVALID : BORDER_OK;
  const cls = `${BASE_CLS} ${borderCls} ${className}`;
  const ariaInvalid = invalid ? { "aria-invalid": true as const } : {};

  if (as === "select") {
    const selectProps = rest as React.SelectHTMLAttributes<HTMLSelectElement>;
    return (
      <select
        ref={ref as React.Ref<HTMLSelectElement>}
        className={`${cls} pr-8 appearance-none bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.25em]`}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2371717a'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/></svg>\")",
        }}
        {...ariaInvalid}
        {...selectProps}
      />
    );
  }
  if (as === "textarea") {
    const taProps = rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>;
    return (
      <textarea
        ref={ref as React.Ref<HTMLTextAreaElement>}
        className={`${cls} resize-y min-h-[80px]`}
        {...ariaInvalid}
        {...taProps}
      />
    );
  }
  const inputProps = rest as React.InputHTMLAttributes<HTMLInputElement>;
  return (
    <input
      ref={ref as React.Ref<HTMLInputElement>}
      type={inputProps.type || "text"}
      className={cls}
      {...ariaInvalid}
      {...inputProps}
    />
  );
});

export default FieldInput;
